import React from 'react';
import HelpBadge from './components/HelpBadge';
import {
  OFAC_NAME_ORDER_HELP,
  OFAC_NAME_STYLES,
  ofacComposedLegalNameHelp,
  ofacNameFieldsInDisplayOrder,
  ofacNameOrderHelpItems,
  ofacNamePartHelp,
  ofacNamePartLabels,
  type OfacLanguage,
  type OfacNameStyle,
} from './ofacIntakeCatalog';
import { syncOfacIntakeLegalName } from './ofacNamedWhitelistUi';

export type OfacNameOrderValue = {
  nameStyle: OfacNameStyle;
  givenName: string;
  givenName2: string;
  surname: string;
  surname2: string;
  entityName: string;
};

type OfacNameOrderIntakeProps = {
  language: OfacLanguage;
  value: OfacNameOrderValue;
  onChange: (next: OfacNameOrderValue) => void;
  compact?: boolean;
  fieldPrefix?: string;
  detected?: boolean;
  /** Research intake passes epoch-scoped, read-only-until-focus props so the browser cannot refill a queued subject. */
  fieldProps?: (field: string) => Record<string, unknown>;
};

const NAME_AUTOFILL_IGNORE = {
  autoComplete: 'off',
  autoCorrect: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
} as const;

export function OfacNameOrderIntake({
  language,
  value,
  onChange,
  compact = false,
  fieldPrefix = '',
  detected = false,
  fieldProps,
}: OfacNameOrderIntakeProps) {
  const help = OFAC_NAME_ORDER_HELP[language];
  const labels = ofacNamePartLabels(value.nameStyle, language);
  const selectedStyle = OFAC_NAME_STYLES.find((row) => row.value === value.nameStyle);
  const orderLabel = compact
    ? (language === 'es' ? 'Cómo está escrito este nombre anterior' : 'How this former name is written')
    : (language === 'es' ? 'Orden del nombre' : 'Name order');
  const composedLabel = language === 'es' ? `${fieldPrefix}Nombre legal compuesto`.trim() : `${fieldPrefix}Composed legal name`.trim();

  const patch = (partial: Partial<OfacNameOrderValue>) => {
    onChange(syncOfacIntakeLegalName({ ...value, ...partial }));
  };

  const labeled = (label: string) => `${fieldPrefix}${label}`.trim();

  return (
    <>
      <div className="dashboard-ofac-geo-field dashboard-ofac-whitelist-intake__span">
        <span className="help-badge-label-row">
          {orderLabel}
          <HelpBadge
            wide
            label={language === 'es' ? `Ayuda sobre ${orderLabel.toLowerCase()}` : `Help about ${orderLabel.toLowerCase()}`}
          >
            <p><strong>{help.overview}</strong></p>
            <ul>
              {ofacNameOrderHelpItems(value.nameStyle, language).map((item) => (
                <li key={item.key} className={item.selected ? 'is-selected' : undefined}>
                  {item.selected ? (language === 'es' ? 'Seleccionado: ' : 'Selected: ') : ''}
                  {item.body}
                </li>
              ))}
            </ul>
            <p>{help.formChange}</p>
          </HelpBadge>
        </span>
        <select
          aria-label={orderLabel}
          value={value.nameStyle}
          onChange={(event) => patch({ nameStyle: event.target.value as OfacNameStyle })}
        >
          {OFAC_NAME_STYLES.map((style) => (
            <option key={style.value} value={style.value}>
              {language === 'es' ? style.labelEs : style.labelEn}
            </option>
          ))}
        </select>
        <span className="dashboard-ofac-whitelist-intake__hint">
          {detected
            ? (language === 'es'
              ? `Detectado: ${selectedStyle?.labelEs || ''}. Cambia el menú si el orden no es el correcto.`
              : `Detected: ${selectedStyle?.labelEn || ''}. Change the dropdown if this name is written differently.`)
            : (language === 'es' ? (selectedStyle?.hintEs || '') : (selectedStyle?.hintEn || ''))}
        </span>
      </div>
      <div className="dashboard-form-grid dashboard-ofac-whitelist-intake__name-grid">
        {ofacNameFieldsInDisplayOrder(value.nameStyle).map((part) => (
          <div key={part} className="dashboard-ofac-geo-field">
            <span className="help-badge-label-row">
              {labels[part]}
              <HelpBadge
                label={
                  language === 'es'
                    ? `Ayuda sobre ${ofacNamePartLabels(value.nameStyle, 'es')[part]}`
                    : `Help about ${ofacNamePartLabels(value.nameStyle, 'en')[part]}`
                }
              >
                <p>{ofacNamePartHelp(value.nameStyle, part, language)}</p>
              </HelpBadge>
            </span>
            <input
              type="text"
              {...NAME_AUTOFILL_IGNORE}
              {...(fieldProps ? fieldProps(part) : {})}
              aria-label={labeled(labels[part])}
              value={value[part]}
              onChange={(event) => patch({ [part]: event.target.value })}
            />
          </div>
        ))}
        {compact ? (
          <p className="research-intake-composed-preview dashboard-ofac-whitelist-intake__span">
            {value.entityName
              ? (language === 'es' ? `Se busca como: ${value.entityName}` : `Searched as: ${value.entityName}`)
              : (language === 'es' ? 'Se busca como alias, no como el nombre legal actual.' : 'Searched as an alias, not the current legal name.')}
          </p>
        ) : (
          <div className="dashboard-ofac-geo-field dashboard-ofac-whitelist-intake__span">
            <span className="help-badge-label-row">
              {language === 'es' ? 'Nombre legal compuesto' : 'Composed legal name'}
              <HelpBadge label={language === 'es' ? 'Ayuda sobre el nombre legal compuesto' : 'Help about composed legal name'}>
                <p>{ofacComposedLegalNameHelp(language)}</p>
              </HelpBadge>
            </span>
            <input
              type="text"
              {...NAME_AUTOFILL_IGNORE}
              {...(fieldProps ? fieldProps('composedLegalName') : {})}
              aria-label={composedLabel}
              value={value.entityName}
              onChange={(event) => onChange({ ...value, entityName: event.target.value })}
              placeholder={language === 'es' ? 'Nombre legal' : 'Legal name'}
            />
          </div>
        )}
      </div>
    </>
  );
}
